import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Icon,
  Box,
  SkeletonBodyText,
  Filters,
  ChoiceList,
  ResourceList,
  ResourceItem,
  Bleed,
} from "@medentic/polaris-react";
import { ShieldCheckMarkIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useCountries } from "@shared/hooks/common/useCountries";

// Import Instructor type from EventInstructors
import { Instructor } from "@features/events/types";
import { SearchableChoiceList } from "@shared/ui";
import { TranslateLink } from "@shared/ui";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import { getLocalizedText } from "@shared/lib";

interface AssignInstructorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedInstructorIds: string[];
  onAssign: (instructorIds: string[]) => void;
  eventInstructorIds?: string[]; // Add this prop to filter instructors to only those assigned to the event
}

export default function AssignInstructorsModal({
  isOpen,
  onClose,
  selectedInstructorIds,
  onAssign,
  eventInstructorIds,
}: AssignInstructorsModalProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { GET } = useApiRequest();
  const { countries } = useCountries();
  const navigate = useNavigate();
  // Local state for modal
  const [localSelectedIds, setLocalSelectedIds] = useState<string[] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [previouslyUsedFilter, setPreviouslyUsedFilter] =
    useState<string>("used");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [hasUserSetFilter, setHasUserSetFilter] = useState(false);

  const selectedIds = localSelectedIds ?? selectedInstructorIds;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch all instructors (no server-side filtering)
  const { data: allInstructors = [], isLoading } = useQuery<Instructor[]>({
    queryKey: ["assign-instructors", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/instructors`),
    enabled: Boolean(hcc && isOpen),
    placeholderData: keepPreviousData,
  });

  const hasUsedInstructors = useMemo(
    () =>
      allInstructors.some(
        (instructor: Instructor) => instructor.isUsedBefore === true
      ),
    [allInstructors]
  );

  const effectivePreviouslyUsedFilter =
    !hasUserSetFilter &&
    previouslyUsedFilter === "used" &&
    !hasUsedInstructors
      ? ""
      : previouslyUsedFilter;

  // Filter instructors locally
  const instructors = useMemo(() => {
    let filtered = [...allInstructors];

    // Filter to only show instructors assigned to the event
    if (eventInstructorIds && eventInstructorIds.length > 0) {
      filtered = filtered.filter((instructor: Instructor) =>
        eventInstructorIds.includes(instructor.id)
      );
    }

    // Apply search filter
    if (debouncedQuery) {
      const searchLower = debouncedQuery.toLowerCase();
      filtered = filtered.filter(
        (instructor: Instructor) =>
          getLocalizedText(instructor.firstname)?.toLowerCase().includes(searchLower) ||
          getLocalizedText(instructor.lastname)?.toLowerCase().includes(searchLower) ||
          instructor.email?.toLowerCase().includes(searchLower) ||
          instructor.currentPosition?.toLowerCase().includes(searchLower)
      );
    }

    // Apply country filter
    if (selectedCountry) {
      filtered = filtered.filter(
        (instructor: Instructor) =>
          instructor.geoAddress?.countryISO3?.toLowerCase() === selectedCountry
      );
    }

    // Apply previously used filter
    if (effectivePreviouslyUsedFilter === "used") {
      filtered = filtered.filter(
        (instructor: Instructor) => instructor.isUsedBefore === true
      );
    } else if (effectivePreviouslyUsedFilter === "notUsed") {
      filtered = filtered.filter(
        (instructor: Instructor) => instructor.isUsedBefore === false
      );
    }

    return filtered;
  }, [
    allInstructors,
    eventInstructorIds,
    debouncedQuery,
    selectedCountry,
    effectivePreviouslyUsedFilter,
  ]);

  // Handle instructor selection
  const handleInstructorToggle = (instructorId: string) => {
    setLocalSelectedIds((prev) => {
      const current = prev ?? selectedInstructorIds;
      return current.includes(instructorId)
        ? current.filter((id) => id !== instructorId)
        : [...current, instructorId];
    });
  };

  const resetModalState = useCallback(() => {
    setLocalSelectedIds(null);
    setSearchQuery("");
    setSelectedCountry("");
    setPreviouslyUsedFilter("used");
    setHasUserSetFilter(false);
  }, []);

  // Handle assign
  const handleAssign = () => {
    onAssign(selectedIds);
    resetModalState();
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    resetModalState();
    onClose();
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCountry("");
    setPreviouslyUsedFilter("");
    setHasUserSetFilter(true);
  };

  // Country options from API
  const countryOptions = useMemo(
    () => [
      { label: t("common.allCountries"), value: "" },
      ...countries.map((country) => ({
        label: country.name,
        value: country.iso3?.toLowerCase(),
      })),
    ],
    [countries, t]
  );

  // Filters configuration
  const filters = useMemo(
    () => [
      {
        key: "country",
        label: t("common.country"),
        filter: (
          <SearchableChoiceList
            label={t("common.country")}
            options={countryOptions}
            value={selectedCountry}
            onChange={(value) => setSelectedCountry(value)}
          />
        ),
        shortcut: true,
      },
      {
        key: "previouslyUsed",
        label: t("common.previouslyUsed"),
        filter: (
          <ChoiceList
            title={t("common.previouslyUsed")}
            titleHidden
            choices={[
              {
                label: t("common.previouslyUsed"),
                value: "used",
              },
              {
                label: t("common.notPreviouslyUsed"),
                value: "notUsed",
              },
            ]}
            selected={
              effectivePreviouslyUsedFilter
                ? [effectivePreviouslyUsedFilter]
                : []
            }
            onChange={(value) => {
              setPreviouslyUsedFilter(value[0] || "");
              setHasUserSetFilter(true); // Mark as manually set by user
            }}
            allowMultiple={false}
          />
        ),
        shortcut: true,
      },
    ],
    [t, selectedCountry, effectivePreviouslyUsedFilter, countryOptions]
  );

  // Applied filters
  const appliedFilters = useMemo(() => {
    const filters = [];
    if (selectedCountry) {
      const countryLabel =
        countryOptions.find((c) => c.value === selectedCountry)?.label ||
        selectedCountry;
      filters.push({
        key: "country",
        label: `${t("common.country")}: ${countryLabel}`,
        onRemove: () => setSelectedCountry(""),
      });
    }
    if (effectivePreviouslyUsedFilter) {
      const filterLabel =
        effectivePreviouslyUsedFilter === "used"
          ? t("common.previouslyUsed")
          : t("common.notPreviouslyUsed");
      filters.push({
        key: "previouslyUsed",
        label: filterLabel,
        onRemove: () => {
          setPreviouslyUsedFilter("");
          setHasUserSetFilter(true); // Mark as manually set by user
        },
      });
    }
    return filters;
  }, [t, selectedCountry, effectivePreviouslyUsedFilter, countryOptions]);

  return (
    <Modal
      open={isOpen}
      onClose={handleCancel}
      title={t("eventDetails.instructors.modal.title")}
      size="large"
      primaryAction={{
        content: t("eventDetails.instructors.modal.assignButton"),
        onAction: handleAssign,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleCancel,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Hint */}
          <TranslateLink
            tone="subdued"
            text={t("eventDetails.instructors.modal.cantFindInstructor")}
            onClick={() => {
              navigate(`/e/${hcc}/instructors`);
            }}
          />
        </BlockStack>

        <Bleed marginInline="400" marginBlock="400">
          {/* Instructors list with integrated filtering */}
          {isLoading ? (
            <Box padding="400">
              <BlockStack gap="200">
                {[1, 2, 3].map((i) => (
                  <InlineStack key={i} gap="200" blockAlign="center">
                    <Box width="24px" minHeight="24px" />
                    <Box minHeight="40px">
                      <SkeletonBodyText lines={1} />
                    </Box>
                    <BlockStack gap="100">
                      <Box
                        width="200px"
                        background="bg-surface"
                        minHeight="16px"
                      />
                      <Box
                        width="150px"
                        background="bg-surface"
                        minHeight="12px"
                      />
                    </BlockStack>
                  </InlineStack>
                ))}
              </BlockStack>
            </Box>
          ) : (
            <ResourceList
              resourceName={{ singular: "instructor", plural: "instructors" }}
              items={instructors}
              selectedItems={selectedIds}
              onSelectionChange={(selection) =>
                setLocalSelectedIds(selection as string[])
              }
              selectable
              filterControl={
                <Filters
                  queryValue={searchQuery}
                  queryPlaceholder={t(
                    "eventDetails.instructors.modal.searchPlaceholder"
                  )}
                  onQueryChange={setSearchQuery}
                  onQueryClear={() => setSearchQuery("")}
                  onClearAll={handleClearFilters}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  closeOnChildOverlayClick
                />
              }
              emptyState={
                <Box padding="400">
                  <Text
                    as="p"
                    variant="bodyMd"
                    alignment="center"
                    tone="subdued"
                  >
                    {t("eventDetails.instructors.modal.noInstructors")}
                  </Text>
                </Box>
              }
              renderItem={(instructor: Instructor) => (
                <ResourceItem
                  id={instructor.id}
                  media={
                    <EventsDefaultAvatar 
                      name={instructor.nameWithPrefix || ""} 
                      singleImage={instructor.avatar}
                    />
                  }
                  accessibilityLabel={`Select ${instructor.nameWithPrefix}`}
                  onClick={() => handleInstructorToggle(instructor.id)}
                  verticalAlignment="center"
                >
                  <InlineStack
                    align="space-between"
                    blockAlign="center"
                    wrap={false}
                  >
                    <BlockStack gap="100">
                      <InlineStack gap="200" align="start" blockAlign="center">
                        <Text
                          as="h3"
                          variant="bodyMd"
                          fontWeight="semibold"
                          breakWord
                        >
                          {instructor.nameWithPrefix}
                        </Text>
                        {instructor.verified && (
                          <Icon source={ShieldCheckMarkIcon} tone="success" />
                        )}
                      </InlineStack>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {getLocalizedText(instructor.geoAddress?.summary)}
                      </Text>
                    </BlockStack>
                    <InlineStack align="end" gap="200">
                      {instructor.isUsedBefore && (
                        <Text as="span" variant="bodySm" tone="subdued">
                          {t("common.previouslyUsed")}
                        </Text>
                      )}
                    </InlineStack>
                  </InlineStack>
                </ResourceItem>
              )}
            />
          )}
        </Bleed>
      </Modal.Section>
    </Modal>
  );
}
